/**
 * Device-bound (ECDSA two-key) request signing for the dashboard.
 *
 * The two-key scheme binds each request to a per-device keypair whose PRIVATE
 * key never leaves the browser. This module owns that device root key:
 *
 *  - It generates a NON-EXTRACTABLE root `CryptoKeyPair` (Web Crypto) once and
 *    persists it in IndexedDB. Non-extractable means the private key can be
 *    used to sign but can never be read out as bytes (no XSS/exfil), yet the
 *    `CryptoKey` handle itself is structured-cloneable so IndexedDB can store
 *    it across sessions.
 *  - On first use it enrols the ROOT PUBLIC key with the backend via
 *    `POST /device-keys`, receiving back the server-derived `root_key_id`
 *    fingerprint. That id is used as the certificate `rootKeyId` so the
 *    authorizer's user-scoped lookup resolves this device's row.
 *  - It exposes a ready {@link TwoKeySigner} (root certifies a short-lived
 *    intermediate that signs each request), consumed by the api-client's
 *    `signRequest` to attach the `X-Auth-*` headers alongside REQUEST-KEY during
 *    the report_only overlap.
 *
 * Enrolment is LAZY and best-effort: a failure (offline, pre-login, an env
 * without the endpoint) never blocks a request — it just means no `X-Auth-*`
 * headers this call, and report_only never denies. Clearing site data drops the
 * key and forces a one-time re-enrolment (acceptable).
 */
import type { ApiClient } from "@authe/fe-commons/api";
import { exportPublicJwk, generateSigningKeyPair, TwoKeySigner } from "@authe/fe-commons/signing";

const DB_NAME = "authe-device-signing";
const STORE = "keys";
const RECORD_KEY = "device-root";

/** Persisted device root material. The keypair is non-extractable; only its
 *  handle + the server-assigned fingerprint are stored. */
interface DeviceKeyRecord {
  rootKeyPair: CryptoKeyPair;
  rootKeyId: string;
}

/** Envelope payload returned by `POST /device-keys` (see enrol_device_key.py). */
interface EnrolEnvelope {
  root_key_id?: string;
  user_id?: string;
}

let client: ApiClient | null = null;
let readySigner: TwoKeySigner | null = null;
let enrolPromise: Promise<TwoKeySigner | null> | null = null;

function isSupported(): boolean {
  return (
    typeof indexedDB !== "undefined" &&
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.subtle !== "undefined"
  );
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadRecord(): Promise<DeviceKeyRecord | null> {
  const db = await openDb();
  try {
    return await new Promise<DeviceKeyRecord | null>((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(RECORD_KEY);
      req.onsuccess = () => resolve((req.result as DeviceKeyRecord | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

async function saveRecord(record: DeviceKeyRecord): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(record, RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Register the app's `ApiClient` so lazy enrolment can `POST /device-keys`.
 * Call once at bootstrap (after the client is created).
 */
export function configureDeviceSigningForApp(apiClient: ApiClient): void {
  client = apiClient;
}

/**
 * The ready device signer, or `null` if enrolment hasn't completed yet. Read
 * SYNCHRONOUSLY on the request hot path (the api-client then awaits the actual
 * signing) so a not-yet-enrolled device simply omits `X-Auth-*` this call.
 */
export function getReadyDeviceSigner(): TwoKeySigner | null {
  return readySigner;
}

/**
 * Idempotently ensure the device is enrolled and a signer is ready. Memoised:
 * concurrent callers share one in-flight enrolment, and once it succeeds the
 * signer is cached for the session. A FAILURE is not memoised (the promise is
 * cleared) so a later call retries — e.g. the first attempts before login fail,
 * then the first authenticated request succeeds.
 *
 * Safe against recursion: the `POST /device-keys` itself flows through the same
 * `signRequest`, but `readySigner` is still `null` during enrolment so that call
 * attaches only REQUEST-KEY (no two-key headers), and this promise is memoised
 * so the nested `ensureDeviceEnrolment()` returns the in-flight promise instead
 * of starting a second enrolment.
 */
export function ensureDeviceEnrolment(): Promise<TwoKeySigner | null> {
  if (readySigner) return Promise.resolve(readySigner);
  if (enrolPromise) return enrolPromise;
  enrolPromise = enrolInternal()
    .then((signer) => {
      readySigner = signer;
      if (!signer) enrolPromise = null; // allow a later retry (e.g. post-login)
      return signer;
    })
    .catch((err) => {
      enrolPromise = null;
      // Best-effort: never surface an enrolment failure to the request path.
      console.warn("device-signing: enrolment failed", err);
      return null;
    });
  return enrolPromise;
}

/**
 * Re-assert a stored device key with the backend, returning the record to use.
 *
 * A persisted record was true when it was written, and stops being true without
 * anything here noticing. The backend row is keyed by `root_key_id` AND the
 * ENROLLING user, so the moment a second account signs in on this browser, the
 * stored certificate names a row that belongs to the first one: the authorizer's
 * lookup misses, two-key verification fails, and every request silently falls
 * back to the weaker symmetric scheme. Nothing recovers, because a record exists
 * so we never enrol again. (Found in sandbox CloudWatch, 2026-09-23: a run of
 * `signing.root_key_not_found` on requests that all succeeded.)
 *
 * `POST /device-keys` is an idempotent upsert of a fingerprint the SERVER
 * derives, so re-sending the same public key is not a second enrolment — it
 * rewrites one row, for whoever is authenticated now. That makes this self-
 * healing for every way the row can go missing (another user, a rebuilt table,
 * a different environment) without this module having to know which happened,
 * or having to guess the current user's id.
 *
 * Best-effort, like enrolment itself: on any failure we keep the stored record,
 * which is exactly today's behaviour. It costs one request per page load, off
 * the hot path — `signRequest` never waits for enrolment, it just omits the
 * `X-Auth-*` headers until a signer is ready.
 */
async function reconfirmEnrolment(record: DeviceKeyRecord): Promise<DeviceKeyRecord | null> {
  if (!client) return null;
  try {
    const publicJwk = await exportPublicJwk(record.rootKeyPair.publicKey);
    const res = await client.post<EnrolEnvelope>(
      "/device-keys",
      { publicJwk },
      { retries: 0, cacheTtlMs: 0 },
    );
    if (!res.ok || !res.data?.root_key_id) return null;
    if (res.data.root_key_id === record.rootKeyId) return record;
    // The id is a fingerprint of this very key, so a change means the server
    // derives it differently than it did when we stored it. Adopt the server's
    // answer — it is the one the authorizer will look up.
    const updated = { rootKeyPair: record.rootKeyPair, rootKeyId: res.data.root_key_id };
    await saveRecord(updated).catch(() => undefined);
    return updated;
  } catch {
    return null; // offline / pre-login: keep signing with what we have
  }
}

async function enrolInternal(): Promise<TwoKeySigner | null> {
  if (!isSupported()) return null;

  // Reuse a previously enrolled device root key across sessions — but confirm
  // the backend still has the row before trusting it (see reconfirmEnrolment).
  const existing = await loadRecord().catch(() => null);
  if (existing) {
    const confirmed = await reconfirmEnrolment(existing);
    return TwoKeySigner.fromRootKeyPair(confirmed ?? existing);
  }

  if (!client) return null;
  // Fresh device: mint a non-extractable root keypair and enrol its PUBLIC key.
  const rootKeyPair = await generateSigningKeyPair(false);
  const publicJwk = await exportPublicJwk(rootKeyPair.publicKey);
  const res = await client.post<EnrolEnvelope>(
    "/device-keys",
    { publicJwk },
    { retries: 0, cacheTtlMs: 0 },
  );
  if (!res.ok || !res.data?.root_key_id) return null;

  const rootKeyId = res.data.root_key_id;
  // Persist AFTER a successful enrolment so we never store a key the backend
  // doesn't know about. A persist failure is non-fatal (we still return a signer
  // for this session; the next load re-enrols).
  await saveRecord({ rootKeyPair, rootKeyId }).catch(() => undefined);
  return TwoKeySigner.fromRootKeyPair({ rootKeyPair, rootKeyId });
}
